import { useRouter } from 'next/router';

import { TabItem, TabList } from '@reckon-web/tabs';
import { NextLink } from '@reckon-web/next-link';
import { makeId } from '@reckon-web/utils';
import { NotificationBadge } from '@reckon-web/badge';

import { useGetNotificationCountForUser } from '../../data/useGetNotificationCountForUser';
import {
  canAdminUsers,
  canEditTemplates,
  canManageJobStatuses,
} from '../../utils/user-security-utils';
import {
  GetMyDetailsQueryUser,
  useGetMyDetails,
} from '../../data/useGetMyDetails';

function getManageHref(user?: GetMyDetailsQueryUser) {
  if (canEditTemplates(user)) return '/manage/templates';
  if (canAdminUsers(user)) return '/manage/users';
  if (canManageJobStatuses(user)) return '/manage/job-statuses';

  return undefined;
}

export const Nav = ({ testId = 'nav' }) => {
  const router = useRouter();
  const { data } = useGetNotificationCountForUser();
  const currentUser = useGetMyDetails();

  const jobRecurrencesCount = data?.jobRecurrences ?? 0;
  const jobSignOffRequestsCount = data?.jobSignOffRequests ?? 0;
  const unreadNotificationsCount = data?.unreadNotifications ?? 0;

  const totalNotifications =
    jobRecurrencesCount + jobSignOffRequestsCount + unreadNotificationsCount;

  const tabs = [
    {
      label: 'Work',
      href: '/',
      isActive: (pathname: string) => pathname === '/',
    },
    {
      label: 'Reports',
      href: '/reports',
      isActive: (pathname: string) => pathname.startsWith('/reports'),
    },
    {
      label: 'Notifications',
      notification: totalNotifications,
      href: '/notifications/home',
      isActive: (pathname: string) => pathname.startsWith('/notifications'),
    },
  ];
  if (currentUser.data?.user) {
    const manageHref = getManageHref(currentUser.data?.user);
    if (manageHref) {
      tabs.push({
        label: 'Manage',
        href: manageHref,
        isActive: (pathname: string) => pathname.startsWith('/manage/'),
      });
    }
  }

  return (
    <TabList css={{ height: '100%' }} data-testid={makeId(testId, 'tab-list')}>
      {tabs.map(({ label, notification, href, isActive }) => {
        return (
          <TabItem
            key={href}
            as={NextLink}
            href={href}
            isSelected={isActive(router.pathname)}
            data-testid={makeId(testId, 'tab-list-item', href)}
          >
            {label}
            {notification ? (
              <NotificationBadge value={notification} tone="critical" />
            ) : null}
          </TabItem>
        );
      })}
    </TabList>
  );
};
